import React, { useState, useEffect } from 'react';
import { Stage, Layer, Rect, Image as KonvaImage, Text, Shape } from 'react-konva';
import trainrailImageSrc from '../../../images/train-rail.png';
import tireImageSrc from '../../../images/pneumatico.png';
import texture1Image from '../../../images/asfalto.png';
import texture2Image from '../../../images/stone-ballast-rail.png';
import texture3Image from '../../../images/subbase.png';
import texture4Image from '../../../images/subgrade.png';
import { useFormFieldValue } from '../../../contexts/FormValuesContext';

interface Props {
    layerHeights?: number[]; // Array con altezze dei 4 strati (reso opzionale con default)
    differenziali?: number[]; // Array con i valori differenziali (reso opzionale con default)
    projectType?: 'container_yard' | 'parking_deck' | string;
    setStabilisedBallastThickness?: number; // Deprecated: now using context for real-time values
}

// Valori di default per le props (4 strati: Sleeper, Ballast, Subballast, Subgrade)
const defaultLayerHeights = [0.25, 0.35, 0.3, 0.65]; // [surface, base, subbase, subgrade]

// 🔧 FIX: Safe wrapper component to handle context hook safely
const TireAndLayersWithContext: React.FC<Props> = props => {
    // Always call the hook here - if context is not available, it will throw and be caught by parent
    const contextThickness = useFormFieldValue<number>('designParameters.setStabilisedBallastThickness');
    return <TireAndLayersCore {...props} contextThickness={contextThickness} />;
};

// Fallback component without context
const TireAndLayersWithoutContext: React.FC<Props> = props => {
    return <TireAndLayersCore {...props} contextThickness={undefined} />;
};

// Core component that receives thickness from either context or props
interface CoreProps extends Props {
    contextThickness?: number;
}

const TireAndLayersCore: React.FC<CoreProps> = ({ layerHeights = defaultLayerHeights, projectType = 'container_yard', setStabilisedBallastThickness = 0.2, contextThickness }) => {
    const rawThickness = contextThickness !== undefined ? contextThickness : setStabilisedBallastThickness;

    // Convert to number and handle both string and number inputs
    const currentThickness = typeof rawThickness === 'number' ? rawThickness : parseFloat(String(rawThickness)) || 0;

    // Calcola l'altezza totale escludendo surface layer (Sleeper - index 0) e subballast se setStabilisedBallastThickness è 0
    const layersTotalHeight = layerHeights.reduce((acc, h, index) => {
        // Escludi surface layer (Sleeper - index 0) per railways
        if (index === 0) {
            return acc;
        }
        // Escludi subballast (index 2) se setStabilisedBallastThickness è 0
        if (index === 2 && currentThickness === 0) {
            return acc;
        }
        return acc + h;
    }, 0);
    const squareWidth = 400; // Larghezza dei rettangoli
    // --- Image constants and dynamic selection ---
    const IMAGE_TOP_PADDING = 2;
    // Dimensions for Container (container_yard)
    const CONTAINER_IMAGE_HEIGHT = 200;
    //const CONTAINER_IMAGE_WIDTH = CONTAINER_IMAGE_HEIGHT * CONTAINER_RAPPORTO_DIAGONALE;
    const CONTAINER_IMAGE_WIDTH = 110;

    // Dimensions for Tire (parking_deck)
    //const TIRE_IMAGE_HEIGHT = TIRE_IMAGE_RADIUS * 2; // originale 350
    //const TIRE_IMAGE_WIDTH = squareWidth - 180; // Adjusted for typical tire aspect
    const TIRE_RAPPORTO_DIAGONALE = 2.2; // 350 / 150
    const TIRE_IMAGE_HEIGHT = 150;
    const TIRE_IMAGE_WIDTH = TIRE_IMAGE_HEIGHT / TIRE_RAPPORTO_DIAGONALE;

    const isParkingDeck = projectType === 'parking_deck';
    const imageSrcToLoad = isParkingDeck ? tireImageSrc : trainrailImageSrc;
    const currentImageHeight = isParkingDeck ? TIRE_IMAGE_HEIGHT : CONTAINER_IMAGE_HEIGHT;
    const currentImageWidth = isParkingDeck ? TIRE_IMAGE_WIDTH : CONTAINER_IMAGE_WIDTH;
    // --- NUOVE COSTANTI PER IL LAYOUT ---
    const titleAreaWidth = 160; // Spazio dedicato ai titoli a sinistra
    const rectStartX = titleAreaWidth; // Inizio dei rettangoli
    const arrowLength = 60; // Lunghezza della freccia
    const arrowGap = 10; // Spazio tra rettangolo e inizio freccia
    const labelGap = 10; // Spazio tra fine freccia ed etichetta 'd'
    const stageWidth = rectStartX + squareWidth + arrowGap + arrowLength + labelGap + 60; // Larghezza totale calcolata + margine
    const stageHeight = IMAGE_TOP_PADDING + currentImageHeight + layersTotalHeight + 10; /* bottom padding */

    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const [textureImg, setTextureImg] = useState<HTMLImageElement | null>(null);
    const [texture2Img, setTexture2Img] = useState<HTMLImageElement | null>(null);
    const [texture3Img, setTexture3Img] = useState<HTMLImageElement | null>(null);
    const [texture4Img, setTexture4Img] = useState<HTMLImageElement | null>(null);

    const titles = ['', 'Ballast', 'Subballast', 'Subgrade'];
    // Etichette da aggiungere
    //const strati = ['d1', 'd2', 'd3', 'd4'];

    useEffect(() => {
        const img = new window.Image();
        img.src = imageSrcToLoad; // Use dynamic image source
        img.onload = () => setImage(img);
        img.onerror = () => {};

        const textures = [texture1Image, texture2Image, texture3Image, texture4Image];
        const setters = [setTextureImg, setTexture2Img, setTexture3Img, setTexture4Img];

        textures.forEach((src, index) => {
            const tex = new window.Image();
            tex.src = src;
            tex.onload = () => setters[index](tex);
            tex.onerror = e => {};
        });
    }, [imageSrcToLoad]);

    return (
        <Stage width={stageWidth} height={stageHeight}>
            <Layer>
                {/* Disegna i rettangoli con il nuovo layout */}
                {layerHeights.map((height, index) => {
                    // Skip surface layer (Sleeper - index 0) for railways
                    if (index === 0) {
                        return null;
                    }

                    // Nascondi lo strato subballast (index 2) quando currentThickness è 0
                    if (index === 2 && currentThickness === 0) {
                        return null;
                    }

                    // Calcolo Y - Since we skip index 0 (Sleeper), start from Ballast
                    let rectY: number;
                    let centerY: number;

                    if (index === 1) {
                        // Ballast: posizionato subito sotto l'immagine (no more Sleeper)
                        rectY = IMAGE_TOP_PADDING + currentImageHeight;
                        centerY = rectY + height / 2;
                    } else {
                        // Altri strati: partono dopo l'immagine + Ballast, calcolando solo i precedenti (escludendo Sleeper e Ballast)
                        // Filtriamo anche lo strato subballast se nascosto nel calcolo delle altezze precedenti
                        const previousLayers = layerHeights.slice(2, index); // Start from index 2 (Subballast) since we skip 0 (Sleeper) and 1 (Ballast)
                        const ballastHeight = layerHeights[1] || 0; // Always include Ballast height
                        const previousLayersHeight = previousLayers.reduce((acc, h, idx) => {
                            const layerIndex = idx + 2; // Adjust index for slice(2, index)
                            // Escludi subballast se currentThickness è 0
                            if (layerIndex === 2 && currentThickness === 0) {
                                return acc;
                            }
                            return acc + h;
                        }, 0);
                        rectY = IMAGE_TOP_PADDING + currentImageHeight + ballastHeight + previousLayersHeight;
                        centerY = rectY + height / 2;
                    }

                    // Calcoli X per il nuovo layout
                    // const arrowStartX = rectStartX + squareWidth + arrowGap;
                    // const arrowEndX = arrowStartX + arrowLength;
                    // const labelX = arrowEndX + labelGap;

                    // Determina quale texture usare per questo strato (ordine corretto)
                    let currentTexture: HTMLImageElement | null = null;
                    if (index === 0) currentTexture = textureImg;
                    else if (index === 1) currentTexture = texture2Img;
                    else if (index === 2) currentTexture = texture3Img;
                    else if (index === 3) currentTexture = texture4Img;

                    // Determina la scala della texture
                    // let textureScale = { x: 0.2, y: 0.2 }; // Scala di default
                    // if (index === 3) {
                    // Se è l'ultimo strato (Subgrade)
                    // textureScale = { x: 0.1, y: 0.1 }; // Usa una scala più piccola per ingrandire la texture
                    // }

                    // Ballast e Subballast: trapezi
                    if ((index === 1 || index === 2) && height > 0) {
                        return (
                            <React.Fragment key={index}>
                                {height > 0 && <Text text={titles[index]} x={5} y={centerY - 6} width={titleAreaWidth - 10} wrap="word" align="right" fontSize={12} fill="black" />}
                                <Shape
                                    sceneFunc={(context, shape) => {
                                        const ballastHeight = layerHeights[1] || 0;
                                        // Considera subballast solo se non nascosto
                                        const subballastHeight = currentThickness === 0 ? 0 : layerHeights[2] || 0;
                                        const totalTrapezoidHeight = ballastHeight + subballastHeight;
                                        const slopeRatio = 0.84; // 40 degrees angle (tan(40°) ≈ 0.84)
                                        const maxSlopeOffset = totalTrapezoidHeight * slopeRatio;
                                        let topOffset, bottomOffset;
                                        if (index === 1) {
                                            // Ballast
                                            topOffset = maxSlopeOffset;
                                            bottomOffset = maxSlopeOffset - ballastHeight * slopeRatio;
                                        } else {
                                            // Subballast (index === 2)
                                            topOffset = maxSlopeOffset - ballastHeight * slopeRatio;
                                            bottomOffset = 0;
                                        }
                                        context.beginPath();
                                        context.moveTo(rectStartX + topOffset, rectY);
                                        context.lineTo(rectStartX + squareWidth, rectY);
                                        context.lineTo(rectStartX + squareWidth, rectY + height);
                                        context.lineTo(rectStartX + bottomOffset, rectY + height);
                                        context.closePath();
                                        if (currentTexture) {
                                            const pattern = context.createPattern(currentTexture, 'repeat');
                                            if (pattern) {
                                                context.fillStyle = pattern;
                                            }
                                        }
                                        context.fill();
                                        context.strokeStyle = 'grey';
                                        context.lineWidth = 0.5;
                                        context.stroke();
                                        context.fillStrokeShape(shape);
                                    }}
                                />
                            </React.Fragment>
                        );
                    }
                    // Subgrade: rettangolo pieno
                    return (
                        <React.Fragment key={index}>
                            {height > 0 && <Text text={titles[index]} x={5} y={centerY - 6} width={titleAreaWidth - 10} wrap="word" align="right" fontSize={12} fill="black" />}
                            <Rect
                                x={rectStartX}
                                y={rectY}
                                width={squareWidth}
                                height={height}
                                fillPatternImage={currentTexture || undefined}
                                fillPatternRepeat="repeat"
                                fillPatternScale={currentTexture ? (index === 3 ? { x: 0.5, y: 0.5 } : { x: 0.2, y: 0.2 }) : undefined}
                                stroke="grey"
                                strokeWidth={0.5}
                            />
                        </React.Fragment>
                    );
                })}

                {/* Immagine Pneumatico */}
                {image && (
                    <KonvaImage
                        image={image}
                        x={(() => {
                            // For railways, center the train on the ballast trapezoid top surface
                            if (projectType !== 'parking_deck') {
                                const ballastHeight = layerHeights[1] || 0;
                                const subballastHeight = currentThickness === 0 ? 0 : layerHeights[2] || 0;
                                const totalTrapezoidHeight = ballastHeight + subballastHeight;
                                const slopeRatio = 0.84; // 40 degrees angle
                                const maxSlopeOffset = totalTrapezoidHeight * slopeRatio;

                                // Calculate ballast top surface position (same as in the Shape component)
                                const ballastTopOffset = maxSlopeOffset; // For ballast layer (index === 1)
                                const ballastTopLeft = rectStartX + ballastTopOffset;
                                const ballastTopRight = rectStartX + squareWidth;
                                const ballastTopWidth = ballastTopRight - ballastTopLeft;
                                const ballastTopCenter = ballastTopLeft + ballastTopWidth / 2;

                                return ballastTopCenter - currentImageWidth / 2;
                            } else {
                                // Default centering for parking deck
                                return rectStartX + squareWidth / 2 - currentImageWidth / 2;
                            }
                        })()} // Centro esatto sopra la superficie del ballast
                        y={IMAGE_TOP_PADDING}
                        width={currentImageWidth}
                        height={currentImageHeight}
                    />
                )}
            </Layer>
        </Stage>
    );
};

// 🔧 FIX: Main component with error boundary pattern to handle context availability
const TireAndLayers: React.FC<Props> = React.memo(function TireAndLayers(props) {
    // Try to use context first, fallback to props-only if context not available
    try {
        return <TireAndLayersWithContext {...props} />;
    } catch (error) {
        // Context not available, use fallback
        return <TireAndLayersWithoutContext {...props} />;
    }
});

export default TireAndLayers;
